import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ListChecks,
  Quote,
  RotateCcw,
  Sparkles,
  X,
} from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import type { AcceptTaskSuggestionInput, MeetingTaskSuggestion } from '../../lib/meetingLogApi';
import { useProjects } from '../../hooks/useProjects';
import { useUsers } from '../../hooks/useUsers';
import { PRIORITY_BADGE, PRIORITY_OPTIONS } from '../tasks/taskConstants';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { formatDate } from '../../lib/format';

/**
 * "Sugestii de sarcini" — the tasks the AI proposed after reading a
 * transcript. Each pending card is a small editable form (title, project,
 * responsible, priority, deadline, details) with two decisions: add it to the
 * project as a real task, or dismiss it. Decided cards move to compact lists
 * below so what was accepted (with a link to the task) and what was ignored
 * stays visible.
 *
 * The panel knows nothing about where the transcript came from: a meeting from
 * the log (MeetingTaskSuggestions) and a call in a team-chat voice room
 * (VoiceCallTaskSuggestions) hand it the same rows and the four decisions, and
 * the team sees the same cards in both places.
 */
export interface TaskSuggestionsActions {
  /** Re-read the transcript, replacing the pending suggestions. */
  generate: () => Promise<unknown>;
  generating: boolean;
  accept: (id: string, input: AcceptTaskSuggestionInput) => Promise<unknown>;
  dismiss: (id: string) => Promise<unknown>;
  restore: (id: string) => Promise<unknown>;
}

export interface TaskSuggestionsPanelProps {
  suggestions: MeetingTaskSuggestion[];
  isLoading?: boolean;
  /** The transcript is still being written or processed — suggestions come last. */
  processing?: boolean;
  /** False when there is no transcript to read yet: the button has nothing to do. */
  hasTranscript: boolean;
  /** Hide the decide/regenerate controls. */
  readOnly?: boolean;
  actions: TaskSuggestionsActions;
  /** The few lines that differ between a meeting and a voice-room call. */
  text?: { hint?: string; empty?: string; needTranscript?: string };
}

const inputCls =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
const labelCls = 'mb-1 block text-xs font-medium text-gray-600';

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function TaskSuggestionsPanel({
  suggestions,
  isLoading = false,
  processing = false,
  hasTranscript,
  readOnly = false,
  actions,
  text,
}: TaskSuggestionsPanelProps) {
  const { t } = useI18n();

  const { data: projectsData } = useProjects({ sort: 'name', limit: 100 });
  const { data: users } = useUsers();
  const projectOptions = useMemo(
    () =>
      (projectsData?.projects ?? []).map((p) => ({
        value: p.id,
        label: p.code ? `${p.code} — ${p.name}` : p.name,
      })),
    [projectsData],
  );
  const userOptions = useMemo(
    () => (users ?? []).map((u) => ({ value: u.id, label: u.name })),
    [users],
  );

  const pending = suggestions.filter((s) => s.status === 'PENDING');
  const accepted = suggestions.filter((s) => s.status === 'ACCEPTED');
  const dismissed = suggestions.filter((s) => s.status === 'DISMISSED');
  const [showDismissed, setShowDismissed] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  // The card of an accepted suggestion just disappears from the pending list
  // and its row lands in "Adăugate" at the very bottom, so with a dozen cards
  // a colleague saw nothing happen (BUG-14). Name the task that was created,
  // right where the click was, with a link to it.
  const [justAcceptedId, setJustAcceptedId] = useState<string | null>(null);
  const justAccepted = justAcceptedId
    ? suggestions.find((s) => s.id === justAcceptedId && s.status === 'ACCEPTED')
    : undefined;

  const needTranscript = text?.needTranscript ?? t('meetings.suggestions.needTranscript');

  async function handleGenerate() {
    setGenerateError(null);
    try {
      await actions.generate();
    } catch (err) {
      setGenerateError(errorText(err, t('meetings.suggestions.generateFailed')));
    }
  }

  return (
    <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <ListChecks size={18} className="text-brand-600" />
            {t('meetings.suggestions.title')}
            {pending.length > 0 && (
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                {pending.length}
              </span>
            )}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {text?.hint ?? t('meetings.suggestions.hint')}
          </p>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={!hasTranscript || processing || actions.generating}
            title={!hasTranscript ? needTranscript : undefined}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Sparkles size={15} className={actions.generating ? 'animate-pulse' : ''} />
            {actions.generating
              ? t('meetings.suggestions.generating')
              : suggestions.length
                ? t('meetings.suggestions.regenerate')
                : t('meetings.suggestions.generate')}
          </button>
        )}
      </div>

      {generateError && (
        <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {generateError}
        </p>
      )}

      {justAccepted && (
        <p
          role="status"
          className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800"
        >
          <Check size={14} className="shrink-0" />
          <span>
            {t('meetings.suggestions.acceptedNotice', {
              task: justAccepted.task?.identifier ?? justAccepted.title,
              project: justAccepted.project?.name ?? '',
            })}
          </span>
          {justAccepted.task && (
            <Link
              to={`/tasks/${encodeURIComponent(justAccepted.task.identifier ?? justAccepted.task.id)}`}
              className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline"
            >
              {t('meetings.suggestions.openTask')}
              <ExternalLink size={12} />
            </Link>
          )}
          <button
            type="button"
            onClick={() => setJustAcceptedId(null)}
            className="ml-auto text-green-700 hover:text-green-900"
            aria-label={t('common.close')}
          >
            <X size={14} />
          </button>
        </p>
      )}

      <div className="mt-4 space-y-3">
        {isLoading ? (
          <p className="text-sm text-gray-400">{t('common.loading')}</p>
        ) : pending.length === 0 ? (
          <p className="rounded-md border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
            {processing
              ? t('meetings.suggestions.processing')
              : !hasTranscript
                ? needTranscript
                : suggestions.length
                  ? t('meetings.suggestions.allDecided')
                  : (text?.empty ?? t('meetings.suggestions.empty'))}
          </p>
        ) : (
          pending.map((s) => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              projectOptions={projectOptions}
              userOptions={userOptions}
              readOnly={readOnly}
              actions={actions}
              onAccepted={() => setJustAcceptedId(s.id)}
            />
          ))
        )}
      </div>

      {accepted.length > 0 && (
        <div className="mt-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t('meetings.suggestions.accepted', { count: accepted.length })}
          </h3>
          <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
            {accepted.map((s) => (
              <li
                key={s.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
              >
                <Check size={14} className="shrink-0 text-green-600" />
                {s.task ? (
                  <Link
                    to={`/tasks/${encodeURIComponent(s.task.identifier ?? s.task.id)}`}
                    className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline"
                  >
                    {s.task.identifier && (
                      <span className="font-mono text-xs text-gray-500">{s.task.identifier}</span>
                    )}
                    {s.title}
                    <ExternalLink size={12} />
                  </Link>
                ) : (
                  <span className="font-medium text-gray-800">{s.title}</span>
                )}
                <span className="text-xs text-gray-500">
                  {[
                    s.project ? (s.project.code ?? s.project.name) : null,
                    s.assignee?.name ?? null,
                    s.decidedBy
                      ? t('meetings.suggestions.decidedBy', { name: s.decidedBy.name })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {dismissed.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowDismissed((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
          >
            {showDismissed ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {t('meetings.suggestions.dismissed', { count: dismissed.length })}
          </button>
          {showDismissed && (
            <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
              {dismissed.map((s) => (
                <DismissedRow key={s.id} suggestion={s} readOnly={readOnly} actions={actions} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

// --- One pending suggestion ---------------------------------------------------------

interface CardProps {
  suggestion: MeetingTaskSuggestion;
  projectOptions: { value: string; label: string }[];
  userOptions: { value: string; label: string }[];
  readOnly: boolean;
  actions: TaskSuggestionsActions;
  onAccepted: () => void;
}

function SuggestionCard({
  suggestion: s,
  projectOptions,
  userOptions,
  readOnly,
  actions,
  onAccepted,
}: CardProps) {
  const { t } = useI18n();

  const [title, setTitle] = useState(s.title);
  const [description, setDescription] = useState(s.description ?? '');
  const [showDescription, setShowDescription] = useState(Boolean(s.description));
  const [projectId, setProjectId] = useState(s.project?.id ?? '');
  const [assigneeId, setAssigneeId] = useState(s.assignee?.id ?? '');
  const [priority, setPriority] = useState<MeetingTaskSuggestion['priority']>(s.priority);
  const [dueDate, setDueDate] = useState(s.dueDate ?? '');
  // Hours, typed as text so "1,5" works the way Romanians write it.
  const [estimate, setEstimate] = useState('');
  const estimateHours = estimate.trim() === '' ? null : Number(estimate.replace(',', '.'));
  const estimateValid =
    estimateHours === null || (Number.isFinite(estimateHours) && estimateHours >= 0);
  const [error, setError] = useState<string | null>(null);
  // Per card, not per panel: deciding on one suggestion must not freeze the rest.
  const [accepting, setAccepting] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const busy = accepting || dismissing;
  const canAccept =
    title.trim().length > 0 && projectId.length > 0 && estimateValid && !busy && !readOnly;

  async function handleAccept() {
    setError(null);
    setAccepting(true);
    try {
      await actions.accept(s.id, {
        title: title.trim(),
        description: description.trim() || null,
        projectId,
        assigneeId: assigneeId || null,
        priority,
        dueDate: dueDate || null,
        estimate: estimateHours,
      });
      onAccepted();
    } catch (err) {
      setError(errorText(err, t('meetings.suggestions.acceptFailed')));
    } finally {
      setAccepting(false);
    }
  }

  async function handleDismiss() {
    setError(null);
    setDismissing(true);
    try {
      await actions.dismiss(s.id);
    } catch (err) {
      setError(errorText(err, t('meetings.suggestions.dismissFailed')));
    } finally {
      setDismissing(false);
    }
  }

  return (
    <article className="rounded-md border border-gray-200 bg-gray-50/60 p-3">
      <div className="flex items-start gap-2">
        <span
          className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${PRIORITY_BADGE[priority]}`}
        >
          {t(`tasks.priority.${priority}`)}
        </span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={readOnly}
          className={`${inputCls} bg-white font-medium`}
          aria-label={t('meetings.suggestions.field.title')}
        />
      </div>

      {s.reason && (
        <p className="mt-2 flex items-start gap-1.5 text-xs italic text-gray-600">
          <Quote size={12} className="mt-0.5 shrink-0 text-gray-400" />
          <span>{s.reason}</span>
        </p>
      )}
      {(s.assigneeHint || s.projectHint) && (
        <p className="mt-1 text-xs text-amber-700">
          {s.assigneeHint && t('meetings.suggestions.assigneeHint', { name: s.assigneeHint })}
          {s.assigneeHint && s.projectHint ? ' · ' : ''}
          {s.projectHint && t('meetings.suggestions.projectHint', { name: s.projectHint })}
        </p>
      )}

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Select
          label={t('meetings.suggestions.field.project')}
          size="sm"
          searchable
          value={projectId}
          onChange={setProjectId}
          options={projectOptions}
          placeholder={t('meetings.suggestions.pickProject')}
          disabled={readOnly}
          required
        />
        <Select
          label={t('meetings.suggestions.field.assignee')}
          size="sm"
          searchable
          clearable
          value={assigneeId}
          onChange={setAssigneeId}
          options={userOptions}
          placeholder={t('meetings.suggestions.noAssignee')}
          disabled={readOnly}
        />
        <Select
          label={t('meetings.suggestions.field.priority')}
          size="sm"
          value={priority}
          onChange={(v) => setPriority(v as MeetingTaskSuggestion['priority'])}
          options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          disabled={readOnly}
        />
        <div>
          <label className={labelCls}>{t('meetings.suggestions.field.dueDate')}</label>
          <DateField
            value={dueDate}
            onChange={setDueDate}
            disabled={readOnly}
            placeholder={formatDate(null)}
          />
        </div>
        <div>
          <label className={labelCls}>{t('meetings.suggestions.field.estimate')}</label>
          <input
            type="text"
            inputMode="decimal"
            value={estimate}
            onChange={(e) => setEstimate(e.target.value)}
            disabled={readOnly}
            placeholder={t('meetings.suggestions.estimatePlaceholder')}
            aria-invalid={!estimateValid}
            className={`ui-select-trigger ui-select-trigger-sm cursor-text ${estimateValid ? '' : 'border-red-400'}`}
          />
        </div>
      </div>

      <div className="mt-3">
        {showDescription ? (
          <>
            <label className={labelCls}>{t('meetings.suggestions.field.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={readOnly}
              className={`${inputCls} bg-white`}
            />
          </>
        ) : (
          !readOnly && (
            <button
              type="button"
              onClick={() => setShowDescription(true)}
              className="text-xs text-brand-700 hover:underline"
            >
              + {t('meetings.suggestions.addDescription')}
            </button>
          )
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => void handleDismiss()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-white disabled:opacity-50"
          >
            <X size={14} /> {t('meetings.suggestions.dismiss')}
          </button>
          <button
            type="button"
            onClick={() => void handleAccept()}
            disabled={!canAccept}
            title={!projectId ? t('meetings.suggestions.pickProject') : undefined}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <Check size={14} />
            {accepting ? t('meetings.suggestions.accepting') : t('meetings.suggestions.accept')}
          </button>
        </div>
      )}
    </article>
  );
}

// --- One dismissed suggestion ------------------------------------------------------

function DismissedRow({
  suggestion: s,
  readOnly,
  actions,
}: {
  suggestion: MeetingTaskSuggestion;
  readOnly: boolean;
  actions: TaskSuggestionsActions;
}) {
  const { t } = useI18n();
  const [restoring, setRestoring] = useState(false);
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
      <span className="text-gray-500 line-through">{s.title}</span>
      {!readOnly && (
        <button
          type="button"
          onClick={() => {
            setRestoring(true);
            void actions.restore(s.id).finally(() => setRestoring(false));
          }}
          disabled={restoring}
          className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline disabled:opacity-50"
        >
          <RotateCcw size={12} /> {t('meetings.suggestions.restore')}
        </button>
      )}
    </li>
  );
}
